import { IndustryModeEnum } from "../../../api/constants";
import {
  RBPayBusinessType,
  RBPayDocument,
  RBPayEmploymentType,
  RBPayPaymentMethod,
  RBPayProfileDashboardModel,
  RBPayPaymentSectionDraft,
  RBPayProfileSectionDraft,
  RBPaySettingsState,
  RBPaySummaryModel,
  RBPayTaxProfile,
} from "../types/rbPaySettings.types";
import {
  businessTypeFromValue,
  defaultBusinessNumber,
  formatBusinessNumber,
  formatSin,
  getBusinessTypeLabel,
  getEmploymentTypeLabel,
  getPaymentMethodLabel,
  getTaxProfileLabel,
  normalizeDateOfBirth,
} from "./rbPaySettings.formatters";
import {
  hasValidationErrors,
  validateRBPayPaymentSection,
  validateRBPayProfileSection,
} from "../validators/rbPaySettings.validators";
import { isRBPayEnabledForAccount } from "../../../util/rbPay";

const getIndustryMode = (account: any, fallbackIndustryMode?: string | null) => {
  const accountIndustryMode =
    account?.pharmacist?.industryMode ||
    account?.networks?.[0]?.industryModeDefault ||
    fallbackIndustryMode ||
    localStorage.getItem("industryMode");

  if (accountIndustryMode === IndustryModeEnum.Pharmaceutical) {
    return IndustryModeEnum.Pharmaceutical;
  }

  return IndustryModeEnum.Dentistry;
};

const inferEmploymentType = (account: any) => {
  const hasBusinessProfile =
    Boolean(account?.pharmacist?.businessName) || Boolean(account?.pharmacist?.taxNumber);

  return hasBusinessProfile ? RBPayEmploymentType.BUSINESS : RBPayEmploymentType.EMPLOYEE;
};

const inferBusinessType = (account: any) => {
  const acceptedTypes = Array.isArray(account?.pharmacist?.acceptedEmploymentTypes)
    ? account.pharmacist.acceptedEmploymentTypes.map((value: string) => String(value).toLowerCase())
    : [];

  return acceptedTypes.some((value: string) => value.includes("incorporated"))
    ? RBPayBusinessType.INCORPORATED
    : RBPayBusinessType.SOLE_PROPRIETOR;
};

const normalizeApprovalStatus = (
  value?: string | null,
  approved: boolean = false
): "not-started" | "pending" | "on-hold" | "active" => {
  if (approved) {
    return "active";
  }

  const normalizedValue = String(value || "").trim().toLowerCase();

  if (normalizedValue === "active") {
    return "active";
  }

  // ON HOLD is a first-class status ("RB Pay NOT active"): the worker keeps a
  // completed profile but cannot use RB Pay. Surfaced as its own pill; the toggle
  // stays disabled (canEnable requires active) and no "Active" state is fabricated.
  if (
    normalizedValue === "on hold" ||
    normalizedValue === "on-hold" ||
    normalizedValue === "on_hold" ||
    normalizedValue === "hold"
  ) {
    return "on-hold";
  }

  if (
    normalizedValue === "pending" ||
    normalizedValue === "pending_review" ||
    normalizedValue === "pending-review" ||
    normalizedValue === "in review" ||
    normalizedValue === "in-review"
  ) {
    return "pending";
  }

  return "not-started";
};

const inferApprovalInfo = (account: any) => {
  const status = normalizeApprovalStatus(account?.pharmacist?.rbPayStatus, false);

  return {
    approved: status === "active",
    status,
  };
};

// RB Pay enablement mirrors Stripe: it's membership in acceptedPaymentMethods
// ("rb_pay" present = enabled). The legacy boolean flags are kept as a fallback
// for backwards-compatibility with any account shape that still carries them.
const inferDashboardEnabled = (account: any) =>
  isRBPayEnabledForAccount(account) ||
  Boolean(
    account?.pharmacist?.isRBPayEnabled ||
      account?.pharmacist?.isRbPayEnabled ||
      account?.pharmacist?.rbPayEnabled ||
      account?.pharmacist?.flags?.isRBPayEnabled ||
      account?.pharmacist?.flags?.isRbPayEnabled
  );

export const createDefaultRBPaySettingsState = (
  account: any,
  fallbackIndustryMode?: string | null
): RBPaySettingsState => {
  const workerEmail = String(account?.email || "").trim();
  const industryMode = getIndustryMode(account, fallbackIndustryMode);
  // Pharma has no Employee/Business toggle in this form — the profile type is
  // fixed by how the worker registered on their account (SIN vs Business number).
  // Derive it from the account (taxNumber/businessName => business, else employee)
  // so `payrollProfileType` is always populated on submit; the BE requires it.
  const employmentType = inferEmploymentType(account);
  const approvalInfo = inferApprovalInfo(account);

  // Pharma has no business-type selector — the type is encoded in the business
  // number's RT/RC code (always RT/Sole Proprietor from the SINInfo registration),
  // so derive it from the number rather than guessing from acceptedEmploymentTypes.
  // Dentistry keeps inferBusinessType as the initial default; the user picks/edits
  // it in the form (which recomposes the RT/RC code to match).
  const seededBusinessType =
    industryMode === IndustryModeEnum.Pharmaceutical
      ? businessTypeFromValue(account?.pharmacist?.taxNumber || "")
      : inferBusinessType(account);

  return {
    workerId: String(account?.id || localStorage.getItem("userId") || ""),
    industryMode,
    workerEmail,
    employmentType,
    paymentMethod: RBPayPaymentMethod.DIRECT_DEPOSIT,
    employeeProfile: {
      // Pharma seeds the SIN from the account's SIN/Business registration, but the
      // worker `/account` view masks `socialInsuranceNumber` to the last 3 digits.
      // The BE exposes the full value on a dedicated `sinForRbPay` key for exactly
      // this flow, so prefer it (fall back to the masked field when it's absent).
      sinNumber: formatSin(
        account?.pharmacist?.sinForRbPay || account?.pharmacist?.socialInsuranceNumber || ""
      ),
      // The backend keeps DOB on pharmacists.date_of_birth and the RB Pay profile
      // merely reads it back, so the account value IS the profile value. Seeding
      // from it means a worker who already has a DOB on file types nothing, and it
      // covers the first-time-setup path where no profile record exists yet.
      dateOfBirth: normalizeDateOfBirth(account?.pharmacist?.dateOfBirth || ""),
      taxProfile: RBPayTaxProfile.HIGH,
    },
    businessProfile: {
      businessType: seededBusinessType,
      legalBusinessName: String(account?.pharmacist?.businessName || ""),
      // Seed the canonical default (000-000-000 RT0001 / RC0001) when none is on
      // file, so a fresh Business profile matches RB-Portal.
      businessNumber:
        formatBusinessNumber(account?.pharmacist?.taxNumber || "") ||
        defaultBusinessNumber(seededBusinessType),
      certificationAccepted: false,
    },
    directDeposit: {
      institutionNumber: "",
      transitNumber: "",
      accountNumber: "",
      document: null,
      authorizationAccepted: false,
    },
    emailTransfer: {
      email: workerEmail,
      certificationAccepted: false,
      emailVerified: false,
      verifiedEmail: "",
      shadowEmail: workerEmail,
    },
    approvalInfo,
    dashboard: {
      enabled: inferDashboardEnabled(account),
    },
    meta: {
      updatedAt: null,
      source: "fe-mock",
    },
  };
};

export const cloneRBPaySettingsState = (state: RBPaySettingsState): RBPaySettingsState =>
  JSON.parse(JSON.stringify(state));

export const buildRBPayProfileSectionDraft = (
  state: RBPaySettingsState
): RBPayProfileSectionDraft => ({
  industryMode: state.industryMode,
  workerEmail: state.workerEmail,
  employmentType: state.employmentType,
  employeeProfile: cloneRBPaySettingsState(state).employeeProfile,
  businessProfile: cloneRBPaySettingsState(state).businessProfile,
});

export const buildRBPayPaymentSectionDraft = (
  state: RBPaySettingsState
): RBPayPaymentSectionDraft => ({
  workerEmail: state.workerEmail,
  paymentMethod: state.paymentMethod,
  directDeposit: cloneRBPaySettingsState(state).directDeposit,
  emailTransfer: cloneRBPaySettingsState(state).emailTransfer,
});

export const mergeRBPayProfileSection = (
  state: RBPaySettingsState,
  profileDraft: RBPayProfileSectionDraft
): RBPaySettingsState => ({
  ...cloneRBPaySettingsState(state),
  employmentType: profileDraft.employmentType,
  employeeProfile: JSON.parse(JSON.stringify(profileDraft.employeeProfile)),
  businessProfile: JSON.parse(JSON.stringify(profileDraft.businessProfile)),
});

export const mergeRBPayPaymentSection = (
  state: RBPaySettingsState,
  paymentDraft: RBPayPaymentSectionDraft
): RBPaySettingsState => ({
  ...cloneRBPaySettingsState(state),
  paymentMethod: paymentDraft.paymentMethod,
  directDeposit: JSON.parse(JSON.stringify(paymentDraft.directDeposit)),
  emailTransfer: JSON.parse(JSON.stringify(paymentDraft.emailTransfer)),
});

export const mergeRBPaySettingsState = (
  account: any,
  persistedState: Partial<RBPaySettingsState> | null,
  fallbackIndustryMode?: string | null
): RBPaySettingsState => {
  const baseState = createDefaultRBPaySettingsState(account, fallbackIndustryMode);

  if (!persistedState) {
    return baseState;
  }

  const mergedApprovalStatus = normalizeApprovalStatus(
    persistedState.approvalInfo?.status || baseState.approvalInfo.status,
    Boolean(persistedState.approvalInfo?.approved || baseState.approvalInfo.approved)
  );

  return {
    ...baseState,
    ...persistedState,
    workerId: String(persistedState.workerId || baseState.workerId),
    industryMode: (persistedState.industryMode || baseState.industryMode) as RBPaySettingsState["industryMode"],
    workerEmail: String(persistedState.workerEmail || baseState.workerEmail),
    employmentType: persistedState.employmentType === undefined
      ? baseState.employmentType
      : persistedState.employmentType,
    employeeProfile: {
      ...baseState.employeeProfile,
      ...persistedState.employeeProfile,
      sinNumber: formatSin(
        (persistedState.employeeProfile && persistedState.employeeProfile.sinNumber) ||
          baseState.employeeProfile.sinNumber
      ),
      dateOfBirth: normalizeDateOfBirth(
        (persistedState.employeeProfile && persistedState.employeeProfile.dateOfBirth) ||
          baseState.employeeProfile.dateOfBirth
      ),
    },
    businessProfile: {
      ...baseState.businessProfile,
      ...persistedState.businessProfile,
      businessNumber: formatBusinessNumber(
        (persistedState.businessProfile && persistedState.businessProfile.businessNumber) ||
          baseState.businessProfile.businessNumber
      ),
    },
    directDeposit: {
      ...baseState.directDeposit,
      ...persistedState.directDeposit,
    },
    emailTransfer: (() => {
      const resolvedEmail =
        (persistedState.emailTransfer && persistedState.emailTransfer.email) ||
        baseState.emailTransfer.email;
      const resolvedVerifiedEmail =
        (persistedState.emailTransfer && persistedState.emailTransfer.verifiedEmail) || "";

      return {
        ...baseState.emailTransfer,
        ...persistedState.emailTransfer,
        email: resolvedEmail,
        shadowEmail: baseState.emailTransfer.shadowEmail,
        verifiedEmail: resolvedVerifiedEmail,
        // An email is only "verified" when the current address matches the
        // verified-on-file address. Recomputed defensively so a returning user
        // with an already-verified email is not forced to re-verify.
        emailVerified: Boolean(resolvedVerifiedEmail) && resolvedEmail === resolvedVerifiedEmail,
      };
    })(),
    approvalInfo: {
      ...baseState.approvalInfo,
      ...persistedState.approvalInfo,
      approved: mergedApprovalStatus === "active",
      status: mergedApprovalStatus,
    },
    dashboard: {
      ...baseState.dashboard,
      ...persistedState.dashboard,
      enabled:
        persistedState.dashboard?.enabled === undefined
          ? baseState.dashboard.enabled
          : Boolean(persistedState.dashboard.enabled),
    },
    meta: {
      ...baseState.meta,
      ...persistedState.meta,
    },
  };
};

export const buildRBPayProfileDashboardModel = (
  state: RBPaySettingsState
): RBPayProfileDashboardModel => {
  const approvalStatus = normalizeApprovalStatus(
    state.approvalInfo.status,
    Boolean(state.approvalInfo.approved)
  );
  const setupStarted =
    Boolean(state.meta.updatedAt) ||
    approvalStatus !== "not-started" ||
    Boolean(state.dashboard.enabled);
  const profileComplete =
    setupStarted && !hasValidationErrors(validateRBPayProfileSection(buildRBPayProfileSectionDraft(state)));
  const paymentComplete =
    setupStarted && !hasValidationErrors(validateRBPayPaymentSection(buildRBPayPaymentSectionDraft(state)));
  const approvalComplete = approvalStatus === "active";
  const completedSteps = [profileComplete, paymentComplete, approvalComplete].filter(Boolean).length;
  const setupComplete = profileComplete && paymentComplete && approvalComplete;
  const progressValue = Math.round((completedSteps / 3) * 100);

  let progressLabel = "Not started";
  let progressDescription = "Open RB Pay to begin your setup.";

  if (setupComplete) {
    progressLabel = "Setup complete";
    progressDescription = "Your RB Pay setup is complete and approved.";
  } else if (approvalStatus === "on-hold") {
    progressLabel = "On hold";
    progressDescription =
      "Your RB Pay is on hold. Review your details or contact support to have it reactivated.";
  } else if (approvalStatus === "pending") {
    progressLabel = "Awaiting approval";
    progressDescription = "Your setup is saved and waiting for admin approval.";
  } else if (setupStarted) {
    progressLabel = `${completedSteps}/3 steps complete`;
    progressDescription =
      completedSteps === 0
        ? "Complete your RB Pay profile and payment method in RB Pay Settings."
        : "Continue in RB Pay Settings to finish the remaining setup steps.";
  }

  const canEnable = setupComplete;
  let toggleHelperText = "Open RB Pay to start the RB Pay setup.";

  if (setupComplete && state.dashboard.enabled) {
    toggleHelperText = "RB Pay is enabled for this worker profile.";
  } else if (setupComplete) {
    toggleHelperText = "RB Pay is approved. Turn it on when you are ready to use it.";
  } else if (approvalStatus === "on-hold") {
    toggleHelperText = "RB Pay is on hold. Contact support to reactivate it before turning RB Pay on.";
  } else if (approvalStatus === "pending") {
    toggleHelperText = "You can enable RB Pay once the RB Pay Status is Active.";
  } else if (setupStarted) {
    toggleHelperText = "Finish the RB Pay setup before turning RB Pay on.";
  }

  return {
    status: approvalStatus,
    statusLabel:
      approvalStatus === "active"
        ? "Active"
        : approvalStatus === "on-hold"
          ? "On Hold"
          : approvalStatus === "pending"
            ? "Pending"
            : "Not Started",
    approvalLabel:
      approvalStatus === "active"
        ? "Approved"
        : approvalStatus === "on-hold"
          ? "On hold"
          : approvalStatus === "pending"
            ? "Pending admin review"
            : "Not submitted",
    progressLabel,
    progressDescription,
    progressValue,
    setupStarted,
    setupComplete,
    canEnable,
    isEnabled: Boolean(state.dashboard.enabled),
    toggleHelperText,
    steps: [
      {
        key: "profile",
        label: "Profile setup",
        description: "RB Pay profile details saved",
        complete: profileComplete,
      },
      {
        key: "payment",
        label: "Payment setup",
        description: "Preferred RB Pay payment method saved",
        complete: paymentComplete,
      },
      {
        key: "approval",
        label: "Approval",
        description: "RB Pay approved by admin",
        complete: approvalComplete,
      },
    ],
  };
};

// ---------------------------------------------------------------------------
// Backend RB Pay worker-profile mapping (/rb-pay/worker-profile).
// FE uses kebab-case enums; the backend uses snake_case/short forms. Enum values
// confirmed against RB-Portal (src/util/workerRbPayroll.js) and the API spec.
// Responses are assumed UNMASKED (BE confirmed). rbPayApproved / emtEmailVerified
// are response-only (admin / EMT-verify controlled) and never sent on write.
// ---------------------------------------------------------------------------
const EMPLOYMENT_TYPE_TO_API: { [key: string]: string } = {
  [RBPayEmploymentType.EMPLOYEE]: "employee",
  [RBPayEmploymentType.BUSINESS]: "business",
};
const EMPLOYMENT_TYPE_FROM_API: { [key: string]: string } = {
  employee: RBPayEmploymentType.EMPLOYEE,
  business: RBPayEmploymentType.BUSINESS,
};
const PAYMENT_METHOD_TO_API: { [key: string]: string } = {
  [RBPayPaymentMethod.DIRECT_DEPOSIT]: "direct_deposit",
  [RBPayPaymentMethod.EMAIL_TRANSFER]: "email_money_transfer",
};
const PAYMENT_METHOD_FROM_API: { [key: string]: string } = {
  direct_deposit: RBPayPaymentMethod.DIRECT_DEPOSIT,
  email_money_transfer: RBPayPaymentMethod.EMAIL_TRANSFER,
};
const TAX_PROFILE_TO_API: { [key: string]: string } = {
  [RBPayTaxProfile.HIGH]: "high",
  [RBPayTaxProfile.LOW]: "low",
};
const TAX_PROFILE_FROM_API: { [key: string]: string } = {
  high: RBPayTaxProfile.HIGH,
  low: RBPayTaxProfile.LOW,
};
const BUSINESS_TYPE_TO_API: { [key: string]: string } = {
  [RBPayBusinessType.SOLE_PROPRIETOR]: "sole_proprietor",
  [RBPayBusinessType.INCORPORATED]: "incorporated",
};
const BUSINESS_TYPE_FROM_API: { [key: string]: string } = {
  sole_proprietor: RBPayBusinessType.SOLE_PROPRIETOR,
  incorporated: RBPayBusinessType.INCORPORATED,
};

// `voidChequeS3Key` arrives as a presigned URL on read (drives preview) or a raw
// object key on write. Keep both: `previewUrl` for viewing, `s3Key` to re-send.
export const buildWorkerVoidChequeDocument = (value?: string | null): RBPayDocument | null => {
  if (!value) {
    return null;
  }

  let s3Key = value;
  let previewUrl = "";
  let name = "void-cheque";

  if (/^https?:\/\//i.test(value)) {
    previewUrl = value;
    try {
      const parsed = new URL(value);
      s3Key = parsed.pathname.replace(/^\//, "");
      name = decodeURIComponent(s3Key.split("/").pop() || name);
    } catch (error) {
      s3Key = value;
    }
  } else {
    name = value.split("/").pop() || name;
  }

  return {
    id: s3Key,
    name,
    type: /\.pdf(\?|$)/i.test(value) ? "application/pdf" : "image/jpeg",
    size: 0,
    previewUrl,
    uploadedAt: "",
    source: "s3",
    s3Key,
  };
};

// Backend worker-profile response -> FE settings state. `base` carries identity /
// industry / workerEmail that the profile response does not include.
export const deserializeWorkerProfile = (
  profile: any,
  account: any,
  fallbackIndustryMode?: string | null
): RBPaySettingsState => {
  const base = createDefaultRBPaySettingsState(account, fallbackIndustryMode);

  if (!profile) {
    return base;
  }

  // Status is the source of truth (rbPayStatus), with rbPayApproved as a transitional
  // fallback. A returned profile with neither reads as pending (a profile exists, not
  // yet approved) — matching the prior behaviour. `approved` tracks ACTIVE only.
  const rawStatus = profile.rbPayStatus ?? profile.rb_pay_status;
  const status =
    rawStatus !== undefined && rawStatus !== null && String(rawStatus).trim() !== ""
      ? normalizeApprovalStatus(rawStatus)
      : Boolean(profile.rbPayApproved)
        ? "active"
        : "pending";
  const approved = status === "active";
  const emtEmail = String(profile.emtEmail || "");
  const emailVerified = Boolean(profile.emtEmailVerified);

  return {
    ...base,
    // Pharma has no profile-type UI — the worker's SIN/Business registration on the
    // account is the source of truth, so always re-derive it. The stored profile can
    // be stale (e.g. created as SIN/employee, then later given a business number), and
    // trusting it would re-persist the wrong payrollProfileType on the next save.
    // Dentistry keeps the saved value (the user picks it in the form).
    employmentType: (base.industryMode === IndustryModeEnum.Pharmaceutical
      ? base.employmentType
      : EMPLOYMENT_TYPE_FROM_API[profile.payrollProfileType] ||
        base.employmentType) as RBPaySettingsState["employmentType"],
    paymentMethod: (PAYMENT_METHOD_FROM_API[profile.preferredPaymentMethod] ||
      base.paymentMethod) as RBPaySettingsState["paymentMethod"],
    employeeProfile: {
      // Pharma: the SIN is owned by the account (source of truth), not this profile.
      // A stored `profile.sin` can be stale or masked (older records persisted the
      // masked last-3 value), so always use the account-seeded SIN; dentistry keeps
      // the saved `profile.sin` that the worker edits in the form.
      sinNumber:
        base.industryMode === IndustryModeEnum.Pharmaceutical
          ? base.employeeProfile.sinNumber
          : formatSin(profile.sin || ""),
      // The profile response mirrors pharmacists.date_of_birth, so it and the
      // account seed are the same value; fall back to the seed when the profile
      // has none (e.g. the record was created as Business, which nulls the DOB).
      dateOfBirth:
        normalizeDateOfBirth(profile.dateOfBirth || "") || base.employeeProfile.dateOfBirth,
      taxProfile: (TAX_PROFILE_FROM_API[profile.taxProfile] ||
        base.employeeProfile.taxProfile) as RBPaySettingsState["employeeProfile"]["taxProfile"],
    },
    businessProfile: {
      // Pharma: business type follows the number's RT/RC code (the source of truth),
      // not a possibly-stale stored businessType. Dentistry uses the saved value.
      businessType: (base.industryMode === IndustryModeEnum.Pharmaceutical
        ? businessTypeFromValue(profile.businessNumber || account?.pharmacist?.taxNumber || "")
        : BUSINESS_TYPE_FROM_API[profile.businessType] ||
          base.businessProfile.businessType) as RBPaySettingsState["businessProfile"]["businessType"],
      legalBusinessName: String(profile.businessName || ""),
      businessNumber: formatBusinessNumber(profile.businessNumber || ""),
      certificationAccepted: Boolean(profile.businessCertificationAccepted),
    },
    directDeposit: {
      institutionNumber: String(profile.bankInstitutionNumber || ""),
      transitNumber: String(profile.bankTransitNumber || ""),
      accountNumber: String(profile.bankAccountNumber || ""),
      document: buildWorkerVoidChequeDocument(profile.voidChequeS3Key),
      authorizationAccepted: Boolean(profile.directDepositAuthorized),
    },
    emailTransfer: {
      email: emtEmail || base.emailTransfer.email,
      certificationAccepted: Boolean(profile.emtEmailCertified),
      emailVerified,
      verifiedEmail: emailVerified ? emtEmail : "",
      shadowEmail: base.emailTransfer.shadowEmail,
    },
    approvalInfo: {
      approved,
      status,
    },
    dashboard: base.dashboard,
    meta: {
      updatedAt: profile.updatedAt || profile.lastUpdatedDate || new Date().toISOString(),
      source: "be",
    },
  };
};

// FE settings state -> backend create/update payload. Sends formatted SIN /
// business number (BE accepts formatted). Omits rbPayApproved & emtEmailVerified
// (response-only). voidChequeS3Key only when a document key is present.
export const serializeWorkerProfile = (state: RBPaySettingsState) => {
  const isPharma = state.industryMode === IndustryModeEnum.Pharmaceutical;

  // Flat superset payload: the BE worker-profile DTO requires the identity fields
  // (sin / businessName / businessNumber / businessCertificationAccepted) for both
  // profile types and selects what it needs by `payrollProfileType`. For pharma the
  // identity values are seeded from the account's SIN/Business registration; they
  // are still sent here because the BE validates (not derives) them.
  const payload: any = {
    payrollProfileType: EMPLOYMENT_TYPE_TO_API[state.employmentType as string],
    sin: state.employeeProfile.sinNumber || "",
    businessType: BUSINESS_TYPE_TO_API[state.businessProfile.businessType],
    businessName: state.businessProfile.legalBusinessName || "",
    businessNumber: state.businessProfile.businessNumber || "",
    // Pharma has no certification checkbox — the business is already registered on
    // the account — so satisfy the BE's "must be true" requirement by forcing it.
    // Non-pharma keeps the value captured by the business form's checkbox.
    businessCertificationAccepted: isPharma
      ? true
      : Boolean(state.businessProfile.certificationAccepted),
    preferredPaymentMethod: PAYMENT_METHOD_TO_API[state.paymentMethod],
    bankInstitutionNumber: state.directDeposit.institutionNumber || "",
    bankTransitNumber: state.directDeposit.transitNumber || "",
    bankAccountNumber: state.directDeposit.accountNumber || "",
    directDepositAuthorized: Boolean(state.directDeposit.authorizationAccepted),
    emtEmail: state.emailTransfer.email || "",
    emtEmailCertified: Boolean(state.emailTransfer.certificationAccepted),
  };

  // Pharma has no tax-profile selector, so the FE only has a placeholder default
  // ('high'). Don't send it for pharma — the BE defaults/derives the tax profile.
  if (!isPharma) {
    payload.taxProfile = TAX_PROFILE_TO_API[state.employeeProfile.taxProfile];
  }

  // Employee-only, both industries. Sent conditionally (unlike the identity
  // superset above) because the BE explicitly doesn't collect a DOB for Business
  // profiles — and saving as Business nulls the stored value, so a stray one here
  // would contradict the record the BE is about to write.
  if (state.employmentType === RBPayEmploymentType.EMPLOYEE) {
    payload.dateOfBirth = state.employeeProfile.dateOfBirth || "";
  }

  if (state.directDeposit.document?.s3Key) {
    payload.voidChequeS3Key = state.directDeposit.document.s3Key;
  }

  return payload;
};

// A saved worker profile exists (so save should PATCH not POST) once the backend
// has assigned a status/timestamp.
export const hasWorkerProfileRecord = (state: RBPaySettingsState) =>
  Boolean(state?.meta?.updatedAt) ||
  (state?.approvalInfo?.status || "not-started") !== "not-started";

export const buildRBPaySummaryModel = (state: RBPaySettingsState): RBPaySummaryModel => {
  const paymentValue = state.paymentMethod === RBPayPaymentMethod.EMAIL_TRANSFER
    ? state.emailTransfer.verifiedEmail || state.emailTransfer.email || state.emailTransfer.shadowEmail || "N/A"
    : state.directDeposit.accountNumber
      ? `Direct Deposit - ${state.directDeposit.accountNumber.slice(-4)}`
      : "Direct Deposit - N/A";

  return {
    approved: Boolean(state.approvalInfo.approved),
    employmentTypeLabel: getEmploymentTypeLabel(state.employmentType),
    paymentMethodLabel: getPaymentMethodLabel(state.paymentMethod),
    paymentValue,
    taxProfileLabel:
      state.employmentType === RBPayEmploymentType.EMPLOYEE
        ? getTaxProfileLabel(state.employeeProfile.taxProfile)
        : undefined,
    businessTypeLabel:
      state.employmentType === RBPayEmploymentType.BUSINESS
        ? getBusinessTypeLabel(state.businessProfile.businessType)
        : undefined,
    legalBusinessName:
      state.employmentType === RBPayEmploymentType.BUSINESS
        ? state.businessProfile.legalBusinessName || "N/A"
        : undefined,
    businessNumber:
      state.employmentType === RBPayEmploymentType.BUSINESS
        ? state.businessProfile.businessNumber || "N/A"
        : undefined,
  };
};
